import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { useControllableOpen } from './use-controllable-open';
const TooltipProvider = TooltipPrimitive.Provider;
/** How long a finger must rest on a trigger before its tooltip shows. */
const LONG_PRESS_MS = 500;
/** Finger travel that turns a press into a scroll and cancels the long-press. */
const LONG_PRESS_SLOP_PX = 8;
/** How long a long-press tooltip stays up after the finger lifts. */
const LONG_PRESS_LINGER_MS = 1500;
/** Per document: whether the most recent input anywhere on the page was a finger. */
const lastInputByDocument = new WeakMap();
/**
 * Start (once per document) tracking whether the user is currently using a
 * finger. Radix opens a tooltip whenever its trigger gains focus, and on touch
 * that focus never comes from the keyboard: it follows a tap on the trigger,
 * or Radix handing focus back to a menu or dialog trigger as it closes, after
 * a tap on one of its items or outside it. That tap landed elsewhere, so this
 * is tracked document-wide. A mouse or pen moving, or any key, hands control
 * back (a hybrid laptop, an iPad with a keyboard).
 */
function trackLastInput(doc) {
    const existing = lastInputByDocument.get(doc);
    if (existing)
        return existing;
    const state = { touch: false };
    const options = { capture: true, passive: true };
    doc.addEventListener('pointerdown', (e) => {
        state.touch = e.pointerType === 'touch';
    }, options);
    doc.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'touch')
            state.touch = false;
    }, options);
    doc.addEventListener('keydown', () => {
        state.touch = false;
    }, options);
    lastInputByDocument.set(doc, state);
    return state;
}
/**
 * The event Radix broadcasts on document as a tooltip opens; every open
 * tooltip closes itself on it, so only one shows at a time. Radix does not
 * export the name.
 */
const RADIX_TOOLTIP_OPEN_EVENT = 'tooltip.open';
const TooltipControlContext = React.createContext(null);
/**
 * Radix tooltips are hover/focus-only: a finger never opens one, yet the focus
 * that follows a tap (Chromium, Android), or that Radix returns to a trigger
 * when its menu closes, opens it and it sticks. So while the user is on touch
 * this tooltip ignores focus and opens on a long-press instead, the way
 * native touch UIs reveal an icon's label. Mouse and keyboard keep Radix's
 * behaviour.
 */
function Tooltip({ open: openProp, defaultOpen, onOpenChange, ...props }) {
    const [open, setOpen] = useControllableOpen({ open: openProp, defaultOpen, onOpenChange });
    const lastInputRef = React.useRef(null);
    const lingerTimerRef = React.useRef(undefined);
    React.useEffect(() => {
        lastInputRef.current = trackLastInput(document);
        return () => clearTimeout(lingerTimerRef.current);
    }, []);
    const control = React.useMemo(() => ({
        onTouchStart: () => clearTimeout(lingerTimerRef.current),
        onLongPress: () => {
            // Radix only announces the opens it drives itself; without this a
            // label still lingering on another trigger stayed up beside this one.
            document.dispatchEvent(new CustomEvent(RADIX_TOOLTIP_OPEN_EVENT));
            setOpen(true);
        },
        onLongPressEnd: () => {
            clearTimeout(lingerTimerRef.current);
            lingerTimerRef.current = setTimeout(() => setOpen(false), LONG_PRESS_LINGER_MS);
        },
        // Radix closes on blur too, but a controlled Root measures that against
        // the last render: focus handed back to the trigger (a menu closing)
        // and taken straight away (the dialog that menu item opened) asks to
        // open and then to close before any render, so Radix dropped the close
        // and the tooltip stuck open over the dialog, eating its Escape.
        onTriggerBlur: () => setOpen(false),
    }), [setOpen]);
    // Radix only asks to open on mouse hover or on focus; the long-press opens
    // through setOpen directly, so this only turns away touch-driven focus.
    const handleOpenChange = React.useCallback((next) => {
        if (next && lastInputRef.current?.touch)
            return;
        setOpen(next);
    }, [setOpen]);
    return (_jsx(TooltipControlContext.Provider, { value: control, children: _jsx(TooltipPrimitive.Root, { open: open, onOpenChange: handleOpenChange, ...props }) }));
}
function TooltipTrigger({ className, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClickCapture, onContextMenu, onBlur, ref, ...props }) {
    const control = React.useContext(TooltipControlContext);
    const pressRef = React.useRef(null);
    const longPressedRef = React.useRef(false);
    const cancelPress = () => {
        clearTimeout(pressRef.current?.timer);
        pressRef.current = null;
    };
    React.useEffect(() => () => clearTimeout(pressRef.current?.timer), []);
    return (_jsx(TooltipPrimitive.Trigger, { ref: ref, 
        // No text selection or iOS callout sheet under a long-pressing finger.
        className: cn('any-pointer-coarse:select-none any-pointer-coarse:[-webkit-touch-callout:none]', className), ...props, onPointerDown: (event) => {
            onPointerDown?.(event);
            // A new press of any kind starts clean: a long-press that no click
            // followed (Chromium fires none) must not swallow the next one.
            longPressedRef.current = false;
            cancelPress();
            if (!control || event.pointerType !== 'touch')
                return;
            control.onTouchStart();
            pressRef.current = {
                x: event.clientX,
                y: event.clientY,
                timer: setTimeout(() => {
                    longPressedRef.current = true;
                    control.onLongPress();
                }, LONG_PRESS_MS),
            };
        }, onPointerMove: (event) => {
            onPointerMove?.(event);
            const press = pressRef.current;
            if (!press || event.pointerType !== 'touch')
                return;
            if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > LONG_PRESS_SLOP_PX) {
                cancelPress();
            }
        }, onPointerUp: (event) => {
            onPointerUp?.(event);
            if (event.pointerType !== 'touch')
                return;
            cancelPress();
            if (longPressedRef.current)
                control?.onLongPressEnd();
        }, onPointerCancel: (event) => {
            onPointerCancel?.(event);
            cancelPress();
            if (longPressedRef.current)
                control?.onLongPressEnd();
        }, onBlur: (event) => {
            onBlur?.(event);
            control?.onTriggerBlur();
        }, 
        // A long-press asked "what is this?"; it must not also press the button.
        onClickCapture: (event) => {
            onClickCapture?.(event);
            if (!longPressedRef.current)
                return;
            longPressedRef.current = false;
            event.preventDefault();
            event.stopPropagation();
        }, onContextMenu: (event) => {
            onContextMenu?.(event);
            // Android raises contextmenu for a long-press; the tooltip answers it.
            if (pressRef.current || longPressedRef.current)
                event.preventDefault();
        } }));
}
function TooltipContent({ ref, className, sideOffset = 4, ...props }) {
    return (_jsx(TooltipPrimitive.Content, { ref: ref, sideOffset: sideOffset, className: cn('animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 bg-popover text-popover-foreground z-50 overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-md', className), ...props }));
}
export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
